import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight, CheckCircle2, CircleAlert, Link2, Pencil, RefreshCw, Truck, Unplug, Warehouse as WarehouseIcon } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import { PageHeader, useTenant } from "@/components/AppShell";
import { Badge, Button, Card, cx, Skeleton } from "@/components/ui";
import { api, canManage, isDemo, type ShippingMethod } from "@/lib/api";
import { ShippingTrackingDialog } from "@/components/ShippingTrackingDialog";
import { fmtRelative } from "@/lib/format";
import { FFN_LINK_ERRORS, ffnLinkError } from "./FfnLink";
import { WarehousesCard } from "@/components/WarehousesCard";
import { intlLocale, t, msg } from "@/lib/i18n";

const STREAMS: Record<string, { label: string; every: string }> = {
  shipments: { label: msg("Versandmeldungen & Tracking"), every: "alle 5 Min." },
  stock: { label: msg("Bestände"), every: "alle 10 Min." },
  inbounds: { label: msg("Wareneingänge"), every: "alle 10 Min." },
};

const ERRORS: Record<string, string> = {
  ...FFN_LINK_ERRORS,
  ffn_not_configured: "Die FFN-Anbindung ist bei Logiful noch nicht eingerichtet.",
  denied: "Der Zugriff wurde bei JTL abgelehnt. Bitte erneut verbinden und den Zugriff erlauben.",
  token: "JTL hat die Anmeldung nicht bestätigt. Bitte erneut versuchen.",
  invalid_state: "Die Verbindung ist abgelaufen. Bitte erneut verbinden.",
  forbidden: "Nur Administratoren können das Lager verbinden.",
  oauth: "Bei der Anmeldung bei JTL ist ein Fehler aufgetreten.",
};

export function WarehousePage() {
  const { tenantId, tenant, role, me } = useTenant();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [choosing, setChoosing] = useState(params.get("choose") === "1");
  const q = useQuery({ queryKey: ["backend", tenantId], queryFn: () => api.backend.get(tenantId) });
  const refresh = () => void qc.invalidateQueries({ queryKey: ["backend", tenantId] });

  // Rückmeldungen aus dem JTL-Login einmalig anzeigen, dann aus der URL entfernen
  useEffect(() => {
    const err = params.get("error");
    if (params.get("connected") === "1") toast.success("FFN-Konto verbunden", { description: params.get("choose") ? "Bitte jetzt das Lager auswählen." : "Der Erstabgleich läuft – Artikel und Bestände erscheinen in wenigen Minuten." });
    if (err) toast.error(err in FFN_LINK_ERRORS ? ffnLinkError(err, params) : (ERRORS[err] ?? "Verbindung fehlgeschlagen"), { duration: 15_000 });
    if (params.get("connected") || err) setParams({}, { replace: true });
  }, [params, setParams]);

  const sync = useMutation({ mutationFn: () => api.backend.sync(tenantId), onSuccess: () => toast.success("Abgleich gestartet", { description: msg("Ergebnisse erscheinen in 1–2 Minuten.") }) });
  const disconnect = useMutation({ mutationFn: () => api.backend.disconnect(tenantId), onSuccess: () => { toast.success("Lager-Verbindung getrennt"); refresh(); } });

  const connect = () => {
    if (isDemo) return toast.info("Demo: Hier würdest du zu JTL weitergeleitet und meldest dich mit dem FFN-Konto an.");
    location.href = api.backend.connectUrl(tenantId);
  };

  const manage = canManage(role);
  const d = q.data;
  const c = d?.connection;
  // Nur von Logiful zugeordnet (ohne Freigabe bei JTL) zählt noch nicht als verbunden
  const connected = c && c.authorized && c.status !== "disconnected";

  return (
    <div className="pb-16">
      <PageHeader title={t("Lager")} description={t("Verbindung von {name} zum Logiful-Lager über das eigene JTL-FFN-Konto.", { name: tenant.name })} />
      <div className="px-4 md:px-8 space-y-4 max-w-4xl">
        {q.isLoading && <Skeleton className="h-48 w-full" />}

        {d && !d.configured && (
          <Notice tone="warning" title={t("FFN-Anbindung noch nicht freigeschaltet")}>
            {me.user.isStaff
              ? <>{t("Logiful-intern: Im JTL-Kundencenter einen OAuth-Client anlegen (Callback")} <code className="font-mono">{location.origin}{t("/ffn/callback")}</code>{t(", Scopes")} <code className="font-mono">{t("ffn.merchant.read ffn.merchant.write")}</code>{t(") und")} <code className="font-mono">{t("FFN_CLIENT_ID")}</code>/<code className="font-mono">{t("FFN_CLIENT_SECRET")}</code> {t("in Render eintragen.")}</>
              : t("Logiful richtet die Verbindung gerade ein. Wir melden uns, sobald es losgehen kann.")}
          </Notice>
        )}

        {d && !connected && (
          <Card className="p-6">
            <div className="flex items-start gap-4">
              <div className="size-11 rounded-xl bg-surface-2 border border-border grid place-items-center"><WarehouseIcon className="size-5 text-fg-muted" /></div>
              <div className="min-w-0">
                <h2 className="text-[16px] font-semibold">{t("Lager noch nicht verbunden")}</h2>
                <p className="text-fg-muted mt-1">{t("Melde dich einmalig mit eurem JTL-FFN-Konto an. Danach laufen Artikel, Aufträge, Bestände und Tracking automatisch.")}</p>
                {c?.merchantId && (
                  <p className="mt-2 text-[12.5px] text-fg-muted">{t("Logiful hat euer FFN-Konto")} <b className="text-fg">{c.merchantCompany ?? c.merchantId}</b> (<span className="font-mono">{c.merchantId}</span>{t(") zugeordnet – bitte genau mit diesem Konto anmelden.")}</p>
                )}
              </div>
            </div>
            <ol className="mt-5 grid sm:grid-cols-3 gap-3">
              {[["1", msg("Bei JTL anmelden"), "mit dem FFN-Konto von " + tenant.name], ["2", msg("Lager wählen"), msg("meist automatisch – es gibt nur eins")], ["3", msg("Fertig"), msg("Erstabgleich startet sofort")]].map(([n, t, s]) => (
                <li key={n} className="rounded-xl border border-border p-3">
                  <span className="size-6 rounded-full bg-ink text-ink-fg grid place-items-center text-[12px] font-semibold">{n}</span>
                  <p className="mt-2 font-medium text-[13px]">{t}</p>
                  <p className="text-[12px] text-fg-muted">{s}</p>
                </li>
              ))}
            </ol>
            <MigrationNotice />
            <div className="mt-5">
              {manage ? (
                <Button variant="primary" className="h-10 px-5" icon={<Link2 className="size-4" />} disabled={!d.configured} onClick={connect}>{t("Mit JTL-FFN verbinden")}</Button>
              ) : (
                <p className="text-[12.5px] text-fg-muted">{t("Nur Administratoren können das Lager verbinden.")}</p>
              )}
            </div>
          </Card>
        )}

        {c && connected && (c.status === "pending" || !c.warehouseId || choosing) && (
          <WarehouseChooser onDone={() => { setChoosing(false); refresh(); }} />
        )}

        {c && (c.status === "active" || c.status === "error") && c.warehouseId && !choosing && (
          <>
            <Card className="p-5">
              <div className="flex items-start gap-4">
                <div className={cx("size-11 rounded-xl grid place-items-center", c.status === "error" ? "bg-danger-soft" : "bg-success-soft")}>{c.status === "error" ? <CircleAlert className="size-5 text-danger" /> : <CheckCircle2 className="size-5 text-success" />}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2"><h2 className="text-[16px] font-semibold">{c.warehouseName}</h2>{c.status === "error" ? <Badge tone="danger">{t("gestört")}</Badge> : <Badge tone="success">{t("verbunden")}</Badge>}</div>
                  <p className="text-[12.5px] text-fg-muted mt-0.5">{t("JTL-FFN · seit")} {new Date(c.connectedAt).toLocaleDateString(intlLocale())} · {c.carriers} {t("Versandarten")}</p>
                </div>
                {manage && (
                  <div className="flex gap-2">
                    {c.status === "error" && <Button size="sm" variant="primary" icon={<Link2 className="size-3.5" />} disabled={!d.configured} onClick={connect}>{t("Neu verbinden")}</Button>}
                    <Button size="sm" icon={<RefreshCw className="size-3.5" />} loading={sync.isPending} onClick={() => sync.mutate()}>{t("Jetzt abgleichen")}</Button>
                  </div>
                )}
              </div>
              {c.lastError && (
                <p className="mt-3 text-[12.5px] text-danger bg-danger-soft rounded-lg px-3 py-2">
                  {c.lastError}
                  {c.status === "error" && manage && <> – <button onClick={connect} className="font-medium underline underline-offset-2">{t("jetzt mit dem FFN-Konto anmelden")}</button></>}
                </p>
              )}
            </Card>

            <Card className="overflow-hidden">
              <div className="px-5 h-11 flex items-center border-b border-border font-semibold text-[13px]">{t("Automatischer Abgleich")}</div>
              <ul className="divide-y divide-border">
                {Object.entries(STREAMS).map(([key, s]) => {
                  const st = c.streams.find((x) => x.stream === key);
                  return (
                    <li key={key} className="flex items-center gap-3 px-5 py-3 text-[13px]">
                      <span className={cx("size-2 rounded-full", st?.lastError ? "bg-danger" : st?.lastRunAt ? "bg-success" : "bg-border-strong")} />
                      <span className="font-medium">{t(s.label)}</span>
                      <span className="text-[12px] text-fg-subtle">{s.every}</span>
                      <span className={cx("ml-auto text-[12px]", st?.lastError ? "text-danger" : "text-fg-muted")}>
                        {st?.lastError ? <span className="inline-flex items-center gap-1"><CircleAlert className="size-3.5" /> {st.lastError.slice(0, 80)}</span> : st?.lastRunAt ? t("zuletzt {v}", { v: fmtRelative(st.lastRunAt) }) : t("startet in Kürze")}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </Card>

            <WarehousesCard editable={manage} />

            <ShippingMethodsCard />

            {manage && (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => setChoosing(true)}>{t("Lager wechseln")}</Button>
                <Button size="sm" variant="ghost" icon={<Unplug className="size-3.5" />} onClick={() => { if (confirm("Verbindung zum Lager wirklich trennen? Aufträge werden dann nicht mehr übertragen.")) disconnect.mutate(); }}>{t("Verbindung trennen")}</Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function WarehouseChooser({ onDone }: { onDone: () => void }) {
  const { tenantId } = useTenant();
  const q = useQuery({ queryKey: ["backend-warehouses", tenantId], queryFn: () => api.backend.warehouses(tenantId) });
  const select = useMutation({
    mutationFn: (id: string) => api.backend.selectWarehouse(tenantId, id),
    onSuccess: () => { toast.success("Lager verbunden", { description: msg("Der Erstabgleich läuft.") }); onDone(); },
    onError: () => toast.error("Lager konnte nicht übernommen werden"),
  });
  return (
    <Card className="p-5">
      <h2 className="font-semibold">{t("Welches Lager soll verwendet werden?")}</h2>
      <p className="text-fg-muted text-[12.5px] mt-0.5">{t("Diese Lager sind im FFN-Konto freigegeben.")}</p>
      <div className="mt-4 space-y-2">
        {q.isLoading && <Skeleton className="h-14 w-full" />}
        {q.isError && <p className="text-danger text-[13px]">{t("FFN ist gerade nicht erreichbar. Bitte in einer Minute erneut versuchen.")}</p>}
        {q.data?.length === 0 && <p className="text-[13px] text-fg-muted">{t("Im FFN-Konto ist noch kein Lager freigegeben. Bitte bei Logiful melden.")}</p>}
        {q.data?.map((w) => (
          <button key={w.warehouseId} disabled={select.isPending} onClick={() => select.mutate(w.warehouseId)} className="w-full flex items-center gap-3 p-3 rounded-xl border border-border hover:border-ink hover:bg-surface-2/50 text-left disabled:opacity-60">
            <WarehouseIcon className="size-5 text-fg-muted" />
            <span className="min-w-0 flex-1">
              <span className="block font-medium text-[13.5px]">{w.name}</span>
              <span className="block text-[12px] text-fg-muted">{[w.city, w.country].filter(Boolean).join(", ")} · <span className="font-mono">{w.warehouseId}</span></span>
            </span>
            <ArrowRight className="size-4 text-fg-subtle" />
          </button>
        ))}
      </div>
    </Card>
  );
}

export const SHIPPING_TYPE: Record<string, string> = {
  Standard: "Standard",
  Expedited: "Express",
  NextDay: "Nächster Tag",
  SecondDay: "2 Tage",
  SameDay: "Selber Tag",
  ByShippingLabelProvider: "Eigenes Label",
};

/** Tracking einer Versandart: hinterlegt (Dienstleister/Link) oder automatische Zuordnung – Logiful-Admin bearbeitet */
function TrackingCell({ method, admin }: { method: ShippingMethod; admin: boolean }) {
  const [open, setOpen] = useState(false);
  const tv = method.tracking;
  const host = tv?.urlTemplate ? (() => { try { return new URL(tv.urlTemplate.replace(/\{[^}]*\}/g, "x")).host.replace(/^www\./, ""); } catch { return "eigener Link"; } })() : null;
  return (
    <span className="flex items-center gap-1.5 min-w-0">
      {tv ? (
        <span className="min-w-0">
          <span className="block truncate">{tv.carrierName || t("automatisch")}</span>
          {host && <span className="block text-[11.5px] text-fg-muted truncate">{t("Link:")} {host}</span>}
        </span>
      ) : (
        <span className="text-fg-subtle">{t("automatisch")}</span>
      )}
      {admin && (
        <>
          <button onClick={() => setOpen(true)} className="ml-auto size-7 grid place-items-center rounded-md text-fg-subtle hover:text-fg hover:bg-surface-2 shrink-0" aria-label={t("Tracking für {name} bearbeiten", { name: method.name })}>
            <Pencil className="size-3.5" />
          </button>
          {open && <ShippingTrackingDialog method={method} open={open} onOpenChange={setOpen} />}
        </>
      )}
    </span>
  );
}

/** Versandarten je Lager, live aus FFN – das aktive Lager zuerst */
function ShippingMethodsCard() {
  const { tenantId, role, me } = useTenant();
  const isStaffAdmin = me.user.staffRole === "staff_admin";
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["shipping-methods", tenantId], queryFn: () => api.backend.shippingMethods(tenantId), staleTime: 5 * 60_000 });
  const sync = useMutation({
    mutationFn: () => api.backend.syncShippingMethods(tenantId),
    onSuccess: (r) => {
      qc.setQueryData(["shipping-methods", tenantId], r);
      toast.success("Versandarten von FFN abgerufen", { description: r.added.length || r.removed.length ? [r.added.length && `neu: ${r.added.join(", ")}`, r.removed.length && `entfernt: ${r.removed.join(", ")}`].filter(Boolean).join(" · ") : "Keine Änderungen." });
    },
    onError: () => toast.error("FFN ist gerade nicht erreichbar"),
  });
  const [showAll, setShowAll] = useState(false);
  const list = [...(q.data?.warehouses ?? [])].sort((a, b) => Number(!!b.primary) - Number(!!a.primary) || Number(b.active) - Number(a.active));
  const visible = showAll ? list : list.filter((w) => w.active);
  const others = list.length - list.filter((w) => w.active).length;

  return (
    <Card className="overflow-hidden">
      <div className="px-5 min-h-11 py-2 flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-border">
        <Truck className="size-4 text-fg-subtle" />
        <span className="font-semibold text-[13px]">{t("Versandarten")}</span>
        <span className="text-[12px] text-fg-subtle">{q.data?.syncedAt ? t("aus JTL-FFN · abgerufen {v}", { v: fmtRelative(q.data.syncedAt) }) : t("aus JTL-FFN")}</span>
        <div className="ml-auto flex flex-wrap items-center gap-3">
          {others > 0 && (
            <button onClick={() => setShowAll(!showAll)} className="text-[12px] font-medium text-fg-muted hover:text-fg whitespace-nowrap">
              {showAll ? t("Nur aktive Lager") : t("Alle Lager anzeigen (+{others})", { others })}
            </button>
          )}
          {canManage(role) && <Button size="sm" icon={<RefreshCw className={cx("size-3.5", sync.isPending && "animate-spin")} />} disabled={sync.isPending} onClick={() => sync.mutate()}>{t("Von FFN abrufen")}</Button>}
        </div>
      </div>
      {q.isLoading && <div className="p-5"><Skeleton className="h-24 w-full" /></div>}
      {q.isError && <p className="px-5 py-4 text-[13px] text-danger">{t("Versandarten konnten nicht geladen werden. FFN ist gerade nicht erreichbar.")}</p>}
      {visible.map((w) => (
        <div key={w.warehouseId} className="border-b border-border last:border-0">
          {(showAll || list.length > 1) && (
            <div className="px-5 pt-3 pb-1 flex items-center gap-2 text-[12.5px]">
              <WarehouseIcon className="size-3.5 text-fg-subtle" />
              <span className="font-medium">{w.name}</span>
              <span className="text-fg-subtle">{[w.city, w.country].filter(Boolean).join(", ")}</span>
              {w.primary ? <Badge tone="success">{t("Hauptlager")}</Badge> : w.active && <Badge tone="success">{t("aktiv")}</Badge>}
            </div>
          )}
          {w.shippingMethods.length === 0 ? (
            <p className="px-5 py-3 text-[13px] text-fg-muted">{t("Für dieses Lager sind in FFN keine Versandarten freigegeben.")}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-[13px]">
                <thead className="text-[11.5px] text-fg-subtle">
                  <tr><th className="h-8 pl-5 pr-3 font-medium">{t("Versandart")}</th><th className="h-8 px-3 font-medium">{t("Dienstleister")}</th><th className="h-8 px-3 font-medium">{t("Art")}</th><th className="h-8 px-3 font-medium">{t("Annahmeschluss")}</th><th className="h-8 px-3 font-medium">{t("Tracking an Shopify")}</th><th className="h-8 pl-3 pr-5 font-medium">{t("ID")}</th></tr>
                </thead>
                <tbody>
                  {w.shippingMethods.map((m) => (
                    <tr key={m.id} className="border-t border-border">
                      <td className="pl-5 pr-3 py-2"><div className="font-medium">{m.name}</div>{m.note && <div className="text-[11.5px] text-fg-muted">{m.note}</div>}</td>
                      <td className="px-3 py-2">{m.carrier ?? "—"}</td>
                      <td className="px-3 py-2">{m.type ? <Badge tone={m.type === "Standard" ? "neutral" : "info"}>{SHIPPING_TYPE[m.type] ?? m.type}</Badge> : "—"}</td>
                      <td className="px-3 py-2 tabular-nums text-fg-muted">{m.cutoffTime ? t("{v} Uhr", { v: m.cutoffTime.slice(0, 5) }) : "—"}</td>
                      <td className="px-3 py-2"><TrackingCell method={m} admin={isStaffAdmin} /></td>
                      <td className="pl-3 pr-5 py-2 font-mono text-[12px] text-fg-muted">{m.id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
    </Card>
  );
}

/** Umstellung von FFN Connect: sonst entstehen Aufträge doppelt im Lager */
function MigrationNotice() {
  return (
    <div className="mt-5 rounded-xl border border-warning/30 bg-warning-soft/60 p-3 text-[12.5px]">
      <p className="font-medium flex items-center gap-1.5"><AlertTriangle className="size-4 text-warning" /> {t("Bisher mit FFN Connect angebunden?")}</p>
      <p className="mt-1 text-fg-muted">{t("Das Lager kann gefahrlos verbunden werden – bestehende Artikel werden über die SKU verknüpft. Den")} <b>{t("Shop")}</b> {t("erst hier verbinden, nachdem FFN Connect für diesen Shop deaktiviert wurde, sonst kommen Aufträge doppelt ins Lager.")}</p>
    </div>
  );
}

function Notice({ tone, title, children }: { tone: "warning"; title: string; children: ReactNode }) {
  return (
    <div className={cx("rounded-xl border p-4 text-[13px]", tone === "warning" && "border-warning/30 bg-warning-soft/60")}>
      <p className="font-medium flex items-center gap-1.5"><AlertTriangle className="size-4 text-warning" /> {t(title)}</p>
      <p className="mt-1 text-fg-muted text-[12.5px]">{children}</p>
    </div>
  );
}
